import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@repo/design-system/utils";
import { Minus, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface QuantityControlProps {
  quantity: number;
  onQuantityChange: (newQuantity: number) => void;
  onRemove?: () => void;
  min?: number;
  max?: number;
  /**
   * Locks the stepper, the input and the quick-select popover (including its
   * Remove entry), for when edits have nothing to land on. Locks only: the
   * caller decides whether and how to dim.
   */
  disabled?: boolean;
  showUnitPrice?: boolean;
  unitPrice?: number;
  formatCurrency?: (value: number) => string;
  className?: string;
  size?: "sm" | "md" | "lg";
  quickSelectOptions?: number[];
}

export function QuantityControl({
  quantity,
  onQuantityChange,
  onRemove,
  min = 1,
  max,
  disabled = false,
  showUnitPrice = false,
  unitPrice,
  formatCurrency = (value) => value.toFixed(2),
  className,
  size = "md",
  // A short ladder that fits without scrolling on a phone; the input still
  // takes any exact quantity by typing. "Remove" is appended below when an
  // onRemove handler is supplied.
  quickSelectOptions = [1, 5, 10, 20, 50]
}: QuantityControlProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [tempValue, setTempValue] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  // A ref, not state: this timer is never rendered, and holding it in state
  // re-rendered every quantity control on the page on each mouse enter/leave
  // and re-ran the cleanup effect with it.
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const sizeClasses = {
    sm: {
      button: "h-5 w-5",
      input: "h-5 w-10 text-xs",
      icon: "h-2.5 w-2.5"
    },
    md: {
      button: "h-6 w-6",
      input: "h-6 w-11 text-xs",
      icon: "h-3 w-3"
    },
    lg: {
      button: "h-8 w-8",
      input: "h-8 w-12 text-sm",
      icon: "h-4 w-4"
    }
  };

  const currentSize = sizeClasses[size];

  // Deferred to the next tick: opening this dialog synchronously from a
  // click/blur handler lets its own dismiss-on-outside-interaction listener
  // catch the tail of that same click/focus gesture, closing it immediately
  // after it opens.
  const openRemoveConfirm = () => {
    setTimeout(() => setShowRemoveConfirm(true), 0);
  };

  const handleDecrement = () => {
    if (quantity <= min && onRemove) {
      openRemoveConfirm();
      return;
    }
    onQuantityChange(Math.max(min, quantity - 1));
  };

  const handleIncrement = () => {
    const newQuantity = max ? Math.min(max, quantity + 1) : quantity + 1;
    onQuantityChange(newQuantity);
  };

  const handleInputChange = (value: string) => {
    setTempValue(value);

    // Allow empty value for deletion
    if (value === "") {
      return;
    }

    // Only allow numeric input (but allow temporary non-numeric during editing)
    if (!/^\d*$/.test(value)) {
      return;
    }

    // If it's a valid number, update immediately for live feedback
    const num = parseInt(value);
    if (!isNaN(num) && num >= min && (!max || num <= max)) {
      onQuantityChange(num);
    }
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsEditing(true);
    setTempValue(quantity.toString());
    // Select all text after a short delay to ensure it works
    setTimeout(() => {
      e.target.select();
    }, 0);
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    const value = tempValue.trim();

    if (value === "" || value === "0") {
      if (onRemove) {
        // Show confirmation dialog instead of immediately removing
        openRemoveConfirm();
        // Reset temp value to current quantity while waiting for confirmation
        setTempValue("");
        return;
      } else {
        // Reset to minimum if no remove handler
        onQuantityChange(min);
      }
    } else {
      const num = parseInt(value);
      if (isNaN(num) || num < min) {
        // Reset to current quantity if invalid
        // No need to call onQuantityChange as it should already be at the right value
      } else if (max && num > max) {
        onQuantityChange(max);
      } else {
        onQuantityChange(num);
      }
    }

    // Clear temp value and editing state
    setTempValue("");
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const value = e.currentTarget.value.trim();
      if ((value === "" || value === "0") && onRemove) {
        // Show confirmation dialog for Enter with 0
        openRemoveConfirm();
        setIsEditing(false);
        setTempValue("");
      } else {
        e.currentTarget.blur(); // Trigger blur to validate and apply value
      }
    } else if (e.key === "Escape") {
      setTempValue(quantity.toString()); // Reset to original value
      setIsEditing(false);
      e.currentTarget.blur();
    }
  };

  const handleQuickSelect = (qty: number) => {
    if (disabled) return;
    onQuantityChange(qty);
    setIsPopoverOpen(false);
  };

  const cancelRemoveRef = useRef<HTMLButtonElement>(null);

  const handleConfirmRemove = async () => {
    if (onRemove) {
      await onRemove();
    }
    setShowRemoveConfirm(false);
  };

  const handleCancelRemove = () => {
    setShowRemoveConfirm(false);
    // Reset to current quantity
    onQuantityChange(quantity);
  };

  const clearHoverTimeout = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    // Don't show dropdown if user is actively editing, or edits are locked
    if (isEditing || disabled) return;

    clearHoverTimeout();

    // Show dropdown after a short delay
    hoverTimeoutRef.current = setTimeout(() => {
      setIsPopoverOpen(true);
    }, 300);
  };

  const handleMouseLeave = () => {
    // Clear timeout if user leaves before delay
    clearHoverTimeout();

    // Hide dropdown after a short delay
    hoverTimeoutRef.current = setTimeout(() => {
      setIsPopoverOpen(false);
    }, 200);
  };

  // Cleanup timeouts on unmount
  useEffect(() => clearHoverTimeout, []);

  return (
    <>
      <div
        className={cn(
          "flex flex-col items-end justify-center gap-1",
          className
        )}
      >
        {/* A flat bordered pill rather than a raised card: it sits inside
            list rows and dialogs alike, and a shadow made it float above
            content it belongs to. Focus is drawn on the whole pill, not the
            input, so the ring follows the pill's own outline. */}
        <div className="flex items-center overflow-hidden rounded-md border border-gray-200 bg-white transition-colors focus-within:border-purple-300 focus-within:ring-2 focus-within:ring-purple-100 hover:border-gray-300">
          <button
            type="button"
            onClick={handleDecrement}
            disabled={disabled}
            className={cn(
              "flex items-center justify-center text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:pointer-events-none",
              currentSize.button
            )}
            aria-label="Decrease quantity"
          >
            <Minus className={currentSize.icon} />
          </button>

          <div
            className="relative flex cursor-text items-center"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <Input
              type="text"
              value={isEditing ? tempValue : quantity.toString()}
              onChange={(e) => handleInputChange(e.target.value)}
              onFocus={handleInputFocus}
              onKeyDown={handleInputKeyDown}
              onBlur={handleInputBlur}
              disabled={disabled}
              className={cn(
                // Dimming of a disabled control is the caller's call (the row
                // dims as a whole); undo the shared Input's own.
                "cursor-text rounded-none border-0 bg-transparent px-1 text-center font-medium tabular-nums text-gray-900 transition-colors hover:bg-gray-50 focus:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 disabled:cursor-text disabled:opacity-100",
                currentSize.input
              )}
              aria-label="Quantity"
            />

            {/* `&& !disabled`: a popover already open when the lock lands
                closes with it. */}
            <Popover
              open={isPopoverOpen && !disabled}
              onOpenChange={setIsPopoverOpen}
            >
              <PopoverTrigger asChild>
                <div className="pointer-events-none absolute inset-0" />
              </PopoverTrigger>
              <PopoverContent
                className="w-32 p-2"
                align="center"
                // Keep dropdown open when hovering over it
                onMouseEnter={clearHoverTimeout}
                onMouseLeave={handleMouseLeave}
              >
                <div className="space-y-1">
                  {quickSelectOptions.map((qty) => (
                    <button
                      key={qty}
                      onClick={() => handleQuickSelect(qty)}
                      className={cn(
                        "w-full rounded px-2 py-1 text-left text-sm transition-colors hover:bg-purple-50",
                        quantity === qty && "bg-purple-100 font-semibold"
                      )}
                    >
                      {qty}
                    </button>
                  ))}
                  {onRemove && (
                    <>
                      <Separator className="my-1" />
                      <button
                        onClick={() => {
                          if (disabled) return;
                          setIsPopoverOpen(false);
                          openRemoveConfirm();
                        }}
                        className="w-full rounded px-2 py-1 text-left text-sm text-red-600 transition-colors hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <button
            type="button"
            onClick={handleIncrement}
            disabled={disabled}
            className={cn(
              "flex items-center justify-center text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:pointer-events-none",
              currentSize.button
            )}
            aria-label="Increase quantity"
          >
            <Plus className={currentSize.icon} />
          </button>
        </div>

        {showUnitPrice && unitPrice !== undefined && (
          <p className="w-full text-center text-xs text-gray-400">
            ${formatCurrency(unitPrice)}/ea
          </p>
        )}
      </div>

      {/* Remove Confirmation Dialog */}
      <Dialog
        open={showRemoveConfirm}
        onOpenChange={(open) => {
          // Closing via Escape / overlay click is a cancel, not a confirm -
          // it has to reset the quantity, not just hide the dialog. Only the
          // close direction is handled: the dialog has no `DialogTrigger`, so
          // it is only ever opened by setting `showRemoveConfirm` directly.
          if (!open) handleCancelRemove();
        }}
      >
        <DialogContent
          className="sm:max-w-[425px]"
          // Start focus on the safe action; Tab moves to "Remove Item",
          // where Enter/Space confirms.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            cancelRemoveRef.current?.focus();
          }}
          onKeyDown={(e) => {
            // Ctrl/Cmd+Enter confirms from anywhere in the dialog. Moving
            // between the two buttons is left to Tab, which the dialog's
            // focus trap already cycles - arrow keys are a menu/listbox
            // convention, not a dialog one.
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void handleConfirmRemove();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Remove Item?</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this item from your cart? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              ref={cancelRemoveRef}
              variant="outline"
              onClick={handleCancelRemove}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmRemove}>
              Remove Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
